import { useState, useEffect, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/browser';
import { getIdToken } from '@lib/auth';
import { generateClient } from 'aws-amplify/data';
// WebContextCheck
import { RunContentType, CheckContentType, WebsiteContent } from '@src/types/apify';
import type { Schema } from '@amplify/data/resource';
import amplifyConfig from '../../amplifyconfiguration.json';

const dataClient = generateClient<Schema>();
const { awsRestApiUrl } = amplifyConfig.custom;

const POLL_INTERVAL = 1500;
const POLL_TIMEOUT = 240000; // 4 minutes

interface UseApifyOptions {
  skip?: boolean;
}

//function useApify(assistantMessage: AssistantMessage, options: UseApifyOptions = {}) {
function useApify(websiteUrl: string, options: UseApifyOptions = {}) {
  const { skip = false } = options;
  const [data, setData] = useState<WebsiteContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const startTime = useRef<Date | null>(null);
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasFetched = useRef(false);

  const isErrorResponse = async (response: Response) => {
    if (!response.ok) {
      try {
        const body = await response.json();
        setError(new Error(`API Error: status: ${response.status} message: ${body?.message || '<none>'}`));
      } catch (error) {
        Sentry.captureException(error, { extra: { error } });
        setError(new Error(`API Error: status: ${response.status} message: <none>`));
      }
      return true;
    }
    return false;
  };

  const upsertWebsiteData = async ({ datasetId, websiteUrl }: { datasetId: string; websiteUrl: string }) => {
    //TODO this logic belongs in the BE
    console.log(`upsertWebsiteData( {datasetId: ${datasetId}, websiteUrl: ${websiteUrl}} )`);
    const { data: websiteData, errors: getErrors } = await dataClient.models.WebsiteData.get({
      url: websiteUrl,
    });
    if (getErrors) throw getErrors; // TODO: better error handling
    // only update if the datasetId has changed
    if (websiteData.url && websiteData.apifyDatasetId !== datasetId) {
      console.log('updating existing websiteData record');
      const { data: updateData, errors: updateErrors } = await dataClient.models.WebsiteData.update({
        url: websiteUrl,
        type: 'APIFY',
        apifyDatasetId: datasetId,
      });
      if (getErrors) throw updateErrors; // TODO: better error handling
      console.log('WebsiteData update: ', updateData, 'errors:', updateErrors);
    }
    if (!websiteData.url) {
      console.log('creating new websiteData record');
      const { data: createData, errors: createError } = await dataClient.models.WebsiteData.create({
        url: websiteUrl,
        type: 'APIFY',
        apifyDatasetId: datasetId,
      });
      if (getErrors) throw createError; // TODO: better error handling
      console.log('WebsiteData create: ', createData, 'errors:', createError);
    }
  };

  const runContent = useCallback(async () => {
    console.log('runContent()...');
    // prevent multiple calls, in development mode, this is called twice
    if (hasFetched.current) return;
    hasFetched.current = true;

    // Ensure to clear any existing polling before starting a new run
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }

    try {
      console.log('starting new run...');
      const url = `${awsRestApiUrl}content-run`;
      const idToken = await getIdToken();
      const runResponse = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ websiteUrl }),
      });
      if (await isErrorResponse(runResponse)) return null;

      const runResult: RunContentType = await runResponse.json();
      console.log('runResult:', runResult);
      return runResult;
    } catch (error) {
      Sentry.captureException(error, { extra: { error } });
      setError(error as Error);
      setLoading(false);
    }
  }, [websiteUrl]);

  const checkContent = useCallback(async (runInfo: RunContentType) => {
    console.log('checkContent()...');

    // don't loop endlessly
    // TODO: test!
    if (startTime.current && new Date().getTime() - startTime.current.getTime() > POLL_TIMEOUT) {
      setError(new Error('Polling timeout exceeded'));
      setLoading(false);
      return;
    }

    try {
      const url = `${awsRestApiUrl}content-check`;
      const idToken = await getIdToken();
      const statusResponse = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          runId: runInfo.runId,
        }),
      });
      if (await isErrorResponse(statusResponse)) return null;

      const statusResult: CheckContentType = await statusResponse.json();
      console.log('new checkContent statusResult:', statusResult);

      if (statusResult.status === 'SUCCEEDED') {
        console.log(`### checkContent() SUCCEEDED !! {websiteUrl: ${websiteUrl}}`);
        pollTimeoutRef.current && clearTimeout(pollTimeoutRef.current);
        hasFetched.current = true;
        await upsertWebsiteData({ datasetId: statusResult.datasetId, websiteUrl: statusResult.websiteUrl });
        setData({
          datasetId: statusResult.datasetId,
          websiteUrl: statusResult.websiteUrl,
        });
        setLoading(false);
      } else {
        // Continue polling
        pollTimeoutRef.current = setTimeout(() => checkContent(runInfo), POLL_INTERVAL);
      }
    } catch (error) {
      Sentry.captureException(error, { extra: { error } });
      setError(error as Error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (skip) {
      console.log('useApify() skipped.');
      return;
    }
    setLoading(true);

    if (websiteUrl) {
      console.log('### have websiteUrl:', websiteUrl);
      const client = generateClient<Schema>();
      // check if we have website content in a saved Apify dataset
      client.models.WebsiteData.get({
        url: websiteUrl,
      }).then(({ data, errors }) => {
        // found a dataset in dynamo, use it
        if (errors) {
          console.error('error getting website data for usApify:', errors);
          return;
        }
        if (data.apifyDatasetId && data.url) {
          setData({
            datasetId: data.apifyDatasetId,
            websiteUrl: data.url,
          });
          setLoading(false);
        } else {
          // TODO: better error handling
          //  if there  was not record in dynamo the data: {} will be returned and that is okay.
          if (data.url) {
            console.error(`Missing apifyDatasetId for website: ${data.url} data: `, data);
          }
        }

        if (!data.apifyDatasetId) {
          // run Apify actor run to crawl website data, it will create a new Apify dataset
          runContent().then((runInfo) => {
            if (runInfo) {
              // set the status os the run we just started, checkContent will poll for completion and set the correct status
              checkContent(runInfo);
            }
          });
        }
      });
    } else {
      console.error('Could not lookup website with website! companyInfo.company?.website is null!');
    }

    return () => {
      // Cleanup polling if component unmounts
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, [skip, websiteUrl, runContent, checkContent]);

  return { data, loading, error };
}

export { useApify };
